import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Injector,
  Input,
  NgZone,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { RateSpinnerComponent } from "shared/base-components/rate-spinner.component";
import { CcyPairPromiseLookupService } from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { SpinDirection } from "shared/base-components/spinner-buttons.component";
import {
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
  UpdateSourceType,
} from "../../../../shared/base-components/number-spinner.component";
import { BfxInputControlStateEnum } from "../../../bfx-interface-controls/models/bfx-interface-controls.enums";
import { BfxFieldNumberDirective } from "../../../../shared/base-components/bfx-field-number";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";

export const SPINNER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxTrailingPipsSpinnerComponent),
  multi: true,
};

@Component({
  selector: "bfx-trailing-pips-spinner",
  templateUrl: "../../../../shared/base-components/html/number-spinner.html",
  providers: [SPINNER_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxTrailingPipsSpinnerComponent extends RateSpinnerComponent {
  private _pair;
  private _invertChanged: boolean = false;
  private _requestPrice: CubiqTS.BigNumber;
  private pairRateConfig: { pipsToPoints: CubiqTS.BigNumber; pointsToPips: CubiqTS.BigNumber } = {
    pipsToPoints: null,
    pointsToPips: null,
  };

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
    currencyPairService: CcyPairPromiseLookupService,
    private ngZone: NgZone,
  ) {
    super(injector, inputDirective, fieldDirective, currencyPairService);
    this.minMaxTouchValid = true;
  }

  @Input() set inverted(value: boolean) {
    this._invertChanged = value != this.isInverted;
    this.isInverted = value;
  }

  get inverted(): boolean {
    return this.isInverted;
  }

  public get requestPrice(): CubiqTS.BigNumber {
    return this._requestPrice;
  }

  @Input()
  public set requestPrice(value: CubiqTS.BigNumber) {
    this._requestPrice = value;

    this.updateMaxConfiguration();
  }

  private updateMaxConfiguration() {
    if (
      this._requestPrice != null &&
      this.pairRateConfig.pipsToPoints != null &&
      this.pairRateConfig.pointsToPips != null
    ) {
      let max = this._requestPrice
        .minus(this.pairRateConfig.pipsToPoints)
        .times(this.pairRateConfig.pointsToPips)
        .integerValue(CubiqTS.BigNumber.ROUND_CEIL);
      let validMax = max.isGreaterThanOrEqualTo(this.min);

      this.max = max;
      this.updateConfiguration();
      if (validMax && this.data.value != null && max.isLessThan(this.data.value)) {
        this.updateValue(max, UpdateSourceType.CONFIG_CHANGE);
      } else if (!validMax) {
        this.updateValue(new CubiqTS.BigNumber(this.min), UpdateSourceType.CONFIG_CHANGE);
      }
      this.formatInputValue();
      this.setControlValue();
    } else if (this.max != null) {
      this.max = null;
      this.updateConfiguration();
    } else {
      return;
    }
  }

  /**
   * Determine the maximum display precision
   * @param pair
   * @returns
   */
  protected override getMaxPrecision(pair: CubiqTS.CurrencyPairImpl): number {
    const pips = this.getTrailingPips(pair);
    return pips;
  }

  getTrailingPips(pair: CubiqTS.CurrencyPairImpl): number {
    let pips = 0;

    if (
      (!this.isFwd || CubiqTS.RequestTrackingType.SPOT_RATE === this.trigger) &&
      !this.isInverted
    ) {
      pips = pair.limitEntrySpotDecimals - pair.pipsQty;
    } else if (!this.isFwd || CubiqTS.RequestTrackingType.SPOT_RATE === this.trigger) {
      pips = pair.limitEntrySpotInvertDecimals - pair.spotInvertDecimalsQty;
    } else if (!this.isInverted) {
      pips = pair.fwdRateDecimalsQty - pair.fwdPipsQty;
    } else {
      pair.fwdInvertDecimalsQty - pair.fwdInvertPipsQty;
    }
    return pips;
  }

  public override getStep(_: CubiqTS.CurrencyPairImpl): CubiqTS.BigNumber {
    return new CubiqTS.BigNumber("10").pow(-this.pipSize);
  }

  protected override calcPairConstants(pair: CubiqTS.CurrencyPairImpl) {
    if (this._pair === pair && !this._invertChanged) return;

    this._pair = pair;
    super.calcPairConstants(pair);
    this.pairRateConfig = {
      pipsToPoints: this.pipsToPoints,
      pointsToPips: this.pointsToPips,
    };

    let maxPrecision = this.getMaxPrecision(pair);
    this.pipSize = this.getTrailingPips(pair);
    this.pointsDigits = maxPrecision;
    this.rateDigits = maxPrecision;
    this.pipsToPoints = new CubiqTS.BigNumber("10").pow(-this.pipSize);
    this.pointsToPips = new CubiqTS.BigNumber("10").pow(this.pipSize);
    this.min = this.isInverted
      ? new CubiqTS.BigNumber(
          pair.trailingStopMinInvPips != null ? pair.trailingStopMinInvPips.toString() : "2",
        )
      : new CubiqTS.BigNumber(
          pair.trailingStopMinPips != null ? pair.trailingStopMinPips.toString() : "2",
        );

    this.updateMaxConfiguration();
    this.hasBeenTouched = false;
    this.setDefaultValue(this.min);
    this.updateConfiguration();
    if (this.data.value == null || this.min.isGreaterThan(this.data.value)) {
      this.updateValue(this.min, UpdateSourceType.CONFIG_CHANGE);
    } else {
      this.formatInputValue();
    }
    this.step = new CubiqTS.BigNumber("10").pow(-this.pipSize);
  }

  public override processButtonSpin(direction: SpinDirection) {
    return super.processButtonSpin(direction);
  }

  ngAfterViewInit() {
    // ngOnChanges won't fire after *ngIf flag changes, so run manually
    this.updateProductCode(this.productCode, this.isFwd);
    super.ngAfterViewInit();
  }

  resetInputControl(): void {
    this._pair = null;
    super.resetInputControl();
  }
}
